import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { type CognitoAuthentication } from "@src/lib/cognito/src/lib/amplifyAuth";
/* eslint-disable no-restricted-imports */
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { getFirebaseSingleton } from "@src/lib/firebase/src";
/* eslint-enable no-restricted-imports */
import type { User } from "firebase/auth";
import { useEffect, useRef } from "react";

const COGNITO_AUTH_SYNC = "useCognitoAuthSync";

/**
 * Hook that synchronizes Firebase authentication with Cognito.
 * Listens to Firebase ID token changes and automatically authenticates with Cognito
 * using the FIREBASE_TOKEN_EXCHANGE custom challenge.
 *
 * - When Firebase user signs in: authenticate with Cognito using Firebase ID token
 * - When Firebase user signs out: sign out from Cognito
 * - Errors in Cognito sync do not block Firebase authentication
 */
export function useCognitoAuthSync(): void {
  const isCognitoSyncEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_AUTH_SYNC, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.ENABLE_COGNITO_AUTH_SYNC],
  });

  const isSyncingRef = useRef(false);
  const lastSyncedUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!isCognitoSyncEnabled) {
      return undefined;
    }

    const cognitoAuth = getCognitoSingleton();
    const firebaseAuth = getFirebaseSingleton();

    const unsubscribe = firebaseAuth.onIdTokenChanged(
      async (firebaseUser: User | null) => {
        if (!isDefined(firebaseUser)) {
          await handleCognitoSignOut(cognitoAuth, lastSyncedUserIdRef.current);
          lastSyncedUserIdRef.current = undefined;
          return;
        }

        if (isSyncingRef.current || lastSyncedUserIdRef.current === firebaseUser.uid) {
          return;
        }

        try {
          isSyncingRef.current = true;
          await syncFirebaseUserWithCognito(cognitoAuth, firebaseUser);
          lastSyncedUserIdRef.current = firebaseUser.uid;
        } catch (error: unknown) {
          logError(APP_V2_APP_EVENTS.COGNITO_FIREBASE_SYNC_ERROR, {
            error,
            metadata: {
              source: COGNITO_AUTH_SYNC,
              firebaseUserId: firebaseUser.uid,
            },
          });
        } finally {
          isSyncingRef.current = false;
        }
      },
      (error: unknown) => {
        logError(APP_V2_APP_EVENTS.FIREBASE_ID_TOKEN_CHANGED_ERROR, {
          error,
          metadata: { source: COGNITO_AUTH_SYNC },
        });
      }
    );

    return () => {
      unsubscribe();
    };
  }, [isCognitoSyncEnabled]);
}

async function handleCognitoSignOut(
  cognitoAuth: CognitoAuthentication,
  lastSyncedUserId: string | undefined
): Promise<void> {
  if (!isDefined(lastSyncedUserId)) {
    return;
  }

  try {
    await cognitoAuth.signOut();
    logEvent(APP_V2_APP_EVENTS.COGNITO_SIGN_OUT_SUCCESS, {
      source: COGNITO_AUTH_SYNC,
    });
  } catch (error: unknown) {
    logError(APP_V2_APP_EVENTS.COGNITO_SIGN_OUT_ERROR, {
      error,
      metadata: { source: COGNITO_AUTH_SYNC },
    });
  }
}

async function syncFirebaseUserWithCognito(
  cognitoAuth: CognitoAuthentication,
  firebaseUser: User
): Promise<void> {
  const cognitoUser = await cognitoAuth.getCurrentUserInfo();
  if (isDefined(cognitoUser)) {
    const isEmailMatch =
      isDefined(firebaseUser.email) &&
      isDefined(cognitoUser.email) &&
      firebaseUser.email.trim().toLowerCase() === cognitoUser.email.trim().toLowerCase();

    const isPhoneMatch =
      isDefined(firebaseUser.phoneNumber) &&
      isDefined(cognitoUser.phoneNumber) &&
      firebaseUser.phoneNumber.trim() === cognitoUser.phoneNumber.trim();

    const isCognitoUserTheSameAsFirebaseUser = isEmailMatch || isPhoneMatch;

    if (isCognitoUserTheSameAsFirebaseUser) {
      return;
    }

    // Don't emit token change, we will create
    await cognitoAuth.signOut();
  }

  const userIdentifier = firebaseUser.phoneNumber ?? firebaseUser.email;
  if (!isDefined(userIdentifier)) {
    throw new Error("User has no email or phone number");
  }

  const firebaseIdToken = await firebaseUser.getIdToken();
  await cognitoAuth.signInWithFirebaseToken({ userIdentifier, firebaseIdToken });

  logEvent(APP_V2_APP_EVENTS.COGNITO_FIREBASE_SYNC_SUCCESS, {
    source: COGNITO_AUTH_SYNC,
    userIdentifier,
  });
}
